/**
 * @ComponentFor TextBlockViewModel
 */
import React from 'react';
import { styled, StyledProps } from '@glitz/react';
import connect from 'Shared/connect';
import { isCompact } from 'Shared/Viewport';
import TextBlock from './TextBlockViewModel.type';
import { Down40x80 } from 'Shared/Icons/ArrowSwirl';
import { LinkButton, ButtonTheme } from 'Shared/TextButtons';
import { TextLabel, ResponsiveSize, Paragraph } from 'Shared/SharedComponents/atoms/Typography';
import { GridWrap } from 'Shared/PageLayout';
import { createGridClasses, createOffsetGridClassLarge } from 'Shared/grid';
import * as style from 'Shared/Style';

type ConnectedPropType = {
  currentBreakpoint: number;
};

type PropType = TextBlock & ConnectedPropType & StyledProps;

const Swirl = styled(Down40x80, {
  marginTop: '24px', 
  color: style.colors.monochrome.black,
})

const DivWrap = styled.div({
  color: style.colors.monochrome.black,
  gap: '16px',
  margin: { y: '80px', },
  ['@media ' + style.mediaMinLarge]: {
    gap: 'initial',
    margin: { y: '120px', }  
  },
})

const GridWrapBase = styled(GridWrap, {
  color: style.colors.monochrome.black,
  gap: '16px',
  margin: { y: '80px', },

  ['@media ' + style.mediaMinLarge]: {
    gap: 'initial',
    margin: { y: '120px', }  
  },
})

const Label = styled(TextLabel, {
  ['@media ' + style.mediaMinLarge]: {
    marginBottom: '24px',
  },
})

const LinkWrap = styled.div({
  display: 'flex',
  gap: '12px',
  marginTop: '16px',
  ['@media ' + style.mediaMinLarge]: {
    gap: '16px',
    marginTop: '24px',
  },
})

const Link = styled(LinkButton, {
  textDecoration: 'none',
})

function TextBlock(props: PropType) {
  const { showArrow, currentBreakpoint } = props;
  const isMobile = isCompact(currentBreakpoint);
  const RootElement = props.noStandardMargins ? DivWrap : GridWrapBase;
  return (
    <RootElement className='grid wsb'>
      <div className={createGridClasses(12, 11, createOffsetGridClassLarge(2))}>
        <Label upperCase>
          {props.label}
        </Label>
      </div>
      <div className={createGridClasses(12, 8, createOffsetGridClassLarge(2))}>
        <Paragraph fontSize={ResponsiveSize.D56_M32}>
          {props.mainIntro}
        </Paragraph>
        {!showArrow && <LinkWrap>
          {props.buttons?.map((button, index) => (
            <Link
              key={index}
              to={button.url}
              text={button.text}
              title={button.text}
              theme={button.background === 'Transparent' ? ButtonTheme.TransparentWhite : ButtonTheme.WhiteToBlack}
            />
          ))}  
        </LinkWrap>
        }
        {showArrow && !isMobile && <Swirl />}
      </div>
      <div className={createGridClasses(12, 2, createOffsetGridClassLarge(11))}>
        <Paragraph fontSize={ResponsiveSize.D16_M16}>
          {props.mainBody}
        </Paragraph>
        {showArrow && isMobile && <Swirl />}
      </div>
    </RootElement>
  );
}

export default connect((state) => ({
  currentBreakpoint: state.currentBreakpoint,
}))(styled(TextBlock));
